import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Flex } from "rebass/styled-components";
import { useSetRecoilState } from "recoil";
import { IoFlash } from "react-icons/io5";

import { useUser } from "MVVM/Hooks/useUser";
import { useTrialCardStatus } from "MVVM/Hooks/useTrialCardStatus";
import { StripePaystationState } from "MVVM/State";
import { analytics } from "MVVM/Analytics";
import type { ClipStatusTimeline } from "../../@types";

// Once per session: the rider is a nudge on the happiest moment, not a nag.
// The value is the claiming trackId so re-renders and pagination keep the
// rider on the card that won the slot.
const RIDER_SESSION_KEY = "clip-ready-trial-rider-shown";

type ClipReadyRiderProps = {
  timeline: ClipStatusTimeline;
};

// Add-card nudge on the ready state of the tracker card, for no-card trial
// users. Successor to the ALL-1835 toast rider: the toast surface was
// unmounted in the April tray redesign and never rendered (ALL-1851), so the
// nudge lives on the surface that replaced it. KeepTheSpeedCTA covers the
// in-progress states; this covers the completed one.
export const ClipReadyRider = ({ timeline }: ClipReadyRiderProps) => {
  const { allstarUser } = useUser();
  const { status: trialCardStatus } = useTrialCardStatus();
  const setStripePaystation = useSetRecoilState(StripePaystationState);
  const [visible, setVisible] = useState(false);

  const isNoCardTrial =
    !!allstarUser?.user?.userGroup?.isTrialPeriod &&
    !!trialCardStatus?.trialing &&
    !trialCardStatus.hasCard;

  // Claim the session slot inside the effect so two cards mounting in the
  // same render can't both show the rider.
  useEffect(() => {
    if (visible || !isNoCardTrial) return;
    const claimedBy = sessionStorage.getItem(RIDER_SESSION_KEY);
    if (claimedBy && claimedBy !== timeline.trackId) return;
    if (!claimedBy) {
      sessionStorage.setItem(RIDER_SESSION_KEY, timeline.trackId);
      analytics.track("Clip Ready Trial Rider - Shown", {
        trackId: timeline.trackId,
        game: timeline.game,
        contentType: timeline.contentType,
        queueName: timeline.queueName,
      });
    }
    setVisible(true);
  }, [visible, isNoCardTrial, timeline]);

  if (!visible) return null;

  const onClick = () => {
    analytics.track("Trial Add Card Clicked", {
      location: "Clip Ready Card",
      trackId: timeline.trackId,
      game: timeline.game,
      contentType: timeline.contentType,
      queueName: timeline.queueName,
    });
    setStripePaystation({
      isModalOpen: true,
      type: "subscribe",
      pendingEmail: false,
      shouldRefreshMtx: false,
      trialCardSetup: true,
    });
  };

  return (
    <RiderRow onClick={onClick}>
      <IoFlash size={12} />
      Add a card to keep clips this fast
      {trialCardStatus?.extended ? "" : ", +7 trial days"}
      <RiderArrow>→</RiderArrow>
    </RiderRow>
  );
};

const RiderRow = styled(Flex)`
  align-items: center;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  color: ${({ theme }) => theme.colors.tierPlat};
  cursor: pointer;
  font-size: 11.5px;
  font-weight: 600;
  gap: 6px;
  margin-top: 8px;
  padding-top: 8px;

  &:hover {
    filter: brightness(1.15);
  }
`;

const RiderArrow = styled.span`
  margin-left: auto;
`;
